import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import "react-quill/dist/quill.snow.css";
import * as React from "react";
import { questionService, submissionService, problemSetService } from "../../services/api";
import "../../styles/components/features/question-management.css";
import { useCourse } from "../../contexts/CourseContext";
import { useUser } from "../../contexts/UserContext";
import PermissionErrorPage from "../shared/permissionError";
import AutograderStatus from "./autograderStatus";

const getCourseId = (course) =>
  course?.CRN ?? course?.id ?? (typeof course === "number" ? course : null);

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;

  try {
    return JSON.parse(value);
  } catch {
    try {
      const normalized = value.replace(/'([^']*)'/g, '"$1"');
      return JSON.parse(normalized);
    } catch {
      return null;
    }
  }
};

const stringifyMaybeJson = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === "object") {
    return JSON.stringify(parsed, null, 2);
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const getPracticeQueryOrder = (question, fallbackIndex) => {
  const testCasesData = parseMaybeJson(question?.testCases);
  const candidates = [
    testCasesData?.order,
    testCasesData?.queryOrder,
    testCasesData?.practiceQueryOrder,
    question?.order,
    question?.queryOrder,
    question?.practiceQueryOrder,
  ];

  for (const candidate of candidates) {
    const numericValue = Number(candidate);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue;
    }
  }

  const functionName = (question?.functionName || question?.function_name || "")
    .toString()
    .trim();
  const match = functionName.match(/practiceQuery(\d+)/i);
  if (match) {
    return Number(match[1]);
  }

  return fallbackIndex + 1;
};

const getQuestionFunctionName = (question, fallbackIndex) => {
  const functionName = [
    question?.functionName,
    question?.function_name,
    question?.name,
    question?.identifier,
  ]
    .find((value) => Boolean((value || "").toString().trim()));

  if (functionName) {
    return functionName.toString().trim();
  }

  const order = getPracticeQueryOrder(question, fallbackIndex);
  return order ? `practiceQuery${order}` : `practiceQuery${fallbackIndex + 1}`;
};

const getQuestionDescription = (question, metadata) => {
  const candidates = [
    question?.description,
    metadata?.description,
    question?.question,
    question?.query,
    question?.prompt,
    question?.functionName,
  ];

  for (const candidate of candidates) {
    const textValue = (candidate || "").toString().trim();
    if (textValue) {
      return textValue;
    }
  }

  return "No description provided.";
};

const getQuestionMetadata = (question, uploadResult, order, functionName) => {
  const possibleContainers = [
    question?.instructionMetadata,
    question?.instructionsMetadata,
    question?.instruction_metadata,
    question?.instructions_metadata,
    uploadResult?.instruction_metadata,
    uploadResult?.instructions_metadata,
    uploadResult?.query_metadata,
    uploadResult?.question_metadata,
    uploadResult?.metadata,
  ].filter((value) => value && typeof value === "object" && !Array.isArray(value));

  const lookupKeys = [
    functionName,
    `practiceQuery${order}`,
    `practice_query_${order}`,
    `practice_query_${order}`.toUpperCase(),
    String(order),
  ].filter(Boolean);

  const merged = {};

  possibleContainers.forEach((container) => {
    lookupKeys.forEach((key) => {
      if (container[key] && typeof container[key] === "object" && !Array.isArray(container[key])) {
        Object.assign(merged, container[key]);
      }
    });

    if (container.practiceQueries && typeof container.practiceQueries === "object") {
      lookupKeys.forEach((key) => {
        if (container.practiceQueries[key] && typeof container.practiceQueries[key] === "object" && !Array.isArray(container.practiceQueries[key])) {
          Object.assign(merged, container.practiceQueries[key]);
        }
      });
    }

    if (container.queries && typeof container.queries === "object") {
      lookupKeys.forEach((key) => {
        if (container.queries[key] && typeof container.queries[key] === "object" && !Array.isArray(container.queries[key])) {
          Object.assign(merged, container.queries[key]);
        }
      });
    }
  });

  return merged;
};

const normalizeUploadedQuestion = (question, uploadResult, index) => {
  const rawTestCases = parseMaybeJson(question?.testCases) || parseMaybeJson(question?.test_cases) || null;
  const rawParameters = parseMaybeJson(question?.parameters) || question?.parameters || null;
  const rawReturns = parseMaybeJson(question?.returns) || question?.returns || null;
  const rawSchema = parseMaybeJson(question?.schema) || question?.schema || rawParameters?.schema || null;
  const functionName = getQuestionFunctionName(question, index);
  const order = getPracticeQueryOrder(question, index);
  const metadata = getQuestionMetadata(question, uploadResult, order, functionName);
  const description = getQuestionDescription(question, metadata);
  const uploadId = question?.upload_id || question?.uploadId || uploadResult?.upload_id || uploadResult?.uploadId || null;

  return {
    ...question,
    ...metadata,
    functionName,
    order,
    upload_id: uploadId,
    description,
    example: question?.example || metadata?.example || "",
    schema: rawSchema,
    parameters: rawParameters,
    returns: rawReturns,
    testCases: rawTestCases,
    createdQuestionId:
      question?.id ||
      question?.question_id ||
      question?.questionID ||
      question?.created_question_id ||
      question?.createdQuestionId ||
      null,
  };
};

const normalizeUploadResponse = (response) => {
  const rawQuestions = Array.isArray(response?.questions)
    ? response.questions
    : Array.isArray(response?.created_questions)
      ? response.created_questions
      : Array.isArray(response?.parsed_questions)
        ? response.parsed_questions
        : Array.isArray(response)
          ? response
          : [];

  const normalizedQuestions = rawQuestions
    .map((question, index) => normalizeUploadedQuestion(question, response, index))
    .sort((left, right) => {
      const orderDifference = (left.order || 0) - (right.order || 0);
      if (orderDifference !== 0) return orderDifference;
      return left.functionName.localeCompare(right.functionName);
    });

  const createdQuestionIds = Array.isArray(response?.created_question_ids)
    ? response.created_question_ids
    : normalizedQuestions.map((question) => question.createdQuestionId).filter(Boolean);

  const storageErrors = Array.isArray(response?.storage_errors) ? response.storage_errors : [];
  const storedCount = Number(response?.stored_count ?? response?.storedCount ?? createdQuestionIds.length ?? 0) || 0;
  const failedCount = Number(response?.failed_count ?? response?.failedCount ?? storageErrors.length ?? 0) || 0;
  const parsedCount = normalizedQuestions.length;
  const uploadId = response?.upload_id || response?.uploadId || normalizedQuestions[0]?.upload_id || null;

  return {
    questions: normalizedQuestions,
    summary: {
      uploadId,
      createdQuestionIds,
      storedCount,
      failedCount,
      parsedCount,
      storageErrors,
      // New response fields from backend for better UI feedback
      practiceQuerySource: response?.practice_query_source || response?.practiceQuerySource || null,
      schemaSource: response?.schema_source || response?.schemaSource || null,
      extractionMessages: response?.extraction_messages || response?.extractionMessages || response?.detail || null,
      isPartialFailure: storedCount < parsedCount || failedCount > 0 || storageErrors.length > 0,
    },
  };
};

const storageErrorMatchesQuestion = (error, question) => {
  if (!error || !question) return false;

  const questionFunctionName = (question.functionName || "").toString().trim().toLowerCase();
  const errorFunctionNames = [
    error.functionName,
    error.function_name,
    error.question_function_name,
    error.question_data?.functionName,
    error.question_data?.function_name,
  ]
    .filter(Boolean)
    .map((value) => value.toString().trim().toLowerCase());

  if (questionFunctionName && errorFunctionNames.includes(questionFunctionName)) {
    return true;
  }

  const questionOrder = Number(question.order);
  const errorOrderCandidates = [
    error.order,
    error.query_order,
    error.practiceQueryOrder,
    error.question_data?.order,
    error.question_data?.testCases?.order,
    error.question_data?.test_cases?.order,
  ];

  if (Number.isFinite(questionOrder)) {
    return errorOrderCandidates.some((candidate) => Number(candidate) === questionOrder);
  }

  return false;
};

const getQuestionStorageErrors = (question, storageErrors) =>
  (Array.isArray(storageErrors) ? storageErrors : []).filter((error) => storageErrorMatchesQuestion(error, question));

const renderJsonBlock = (value) => {
  const textValue = stringifyMaybeJson(value);
  if (!textValue) return null;

  return <pre className="manage-questions-code-block">{textValue}</pre>;
};

const AddQuestionForm = () => {
  const { selectedCourse } = useCourse();
  const { roleData } = useUser();
  const [questions, setQuestions] = useState([]);
  const [problemSets, setProblemSets] = useState([]);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [markdownFile, setMarkdownFile] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [uploadedQuestions, setUploadedQuestions] = useState([]);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadTopicName, setUploadTopicName] = useState("");
  const [uploadLanguage, setUploadLanguage] = useState("python");
  const [sqlQueryFile, setSqlQueryFile] = useState(null);
  const [sqlQueryField, setSqlQueryField] = useState("student_file");
  const [sqlSchemaFile, setSqlSchemaFile] = useState(null);
  const [sqlInitFile, setSqlInitFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const handleAddQuestionClick = () => {
    setShowAddQuestionModal(true);
    setUploadTopicName("");
    setUploadLanguage("python");
    setUploadedFile(null);
    setMarkdownFile(null);
    setSqlQueryFile(null);
    setSqlQueryField("student_file");
    setSqlSchemaFile(null);
    setSqlInitFile(null);
    setUploadError(null);
  };

const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setUploadError(null);
    }
  };

  const handleMarkdownFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMarkdownFile(file);
      setUploadError(null);
    }
  };

  const handleSqlExamFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSqlSchemaFile(file);
      setUploadError(null);
    }
  };

  const handleSqlInstructionsFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSqlQueryFile(file);
      setUploadError(null);
    }
  };

  const handleSqlInitFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSqlInitFile(file);
      setUploadError(null);
    }
  };

  const handleAutograderUpload = async () => {
    if (!uploadTopicName || !uploadTopicName.trim()) {
      alert("Please enter a Problem Set / Topic name");
      return;
    }

    if (uploadLanguage === "python") {
      if (!uploadedFile) {
        alert("Please select a ZIP file to upload");
        return;
      }
      if (!markdownFile) {
        alert("Please select a markdown file to upload");
        return;
      }
    } else {
      if (!sqlQueryFile || !sqlInitFile || !sqlSchemaFile) {
        alert("Please choose all three files: the question prompts PDF, the init file and the student template.");
        return;
      }
    }

    setIsUploading(true);
    try {
      const courseId = getCourseId(selectedCourse);
      if (!courseId) {
        toast.error("No course selected. Please select a course first.");
        return;
      }
      const result = uploadLanguage === "python"
        ? await questionService.uploadAutograder(
            uploadedFile,
            markdownFile,
            uploadTopicName.trim(),
            courseId
          )
        : await questionService.uploadSqlExam({
            zipFile: null,
            sourceFile: sqlSchemaFile,
            sourceField: "student_file",
            instructionsFile: sqlQueryFile,
            initFile: sqlInitFile,
            uploadTopicName: uploadTopicName.trim(),
            courseId,
          });
      console.log("Upload result:", result);
      const normalizedUpload = normalizeUploadResponse(result);

      console.log("Questions found:", normalizedUpload.questions);
      console.log("TopicName used:", result.uploadTopicName || result.topic);

      if (normalizedUpload.summary.storageErrors.length > 0) {
        console.error("=== STORAGE ERRORS ===");
        normalizedUpload.summary.storageErrors.forEach((error, index) => {
          console.error(`\n❌ Error ${index + 1} - ${error.functionName || error.question_data?.functionName || 'unknown'}:`);
          console.error(`   Error Type: ${error.error || error.detail || 'Unknown error'}`);
          if (error.status_code) {
            console.error(`   HTTP Status: ${error.status_code}`);
          }
          if (error.detail) {
            console.error(`   Detail: ${error.detail}`);
          }
          if (error.question_data) {
            console.error(`   Question Data:`, error.question_data);
          }
        });
        console.error("======================\n");
      }

      setUploadedQuestions(normalizedUpload.questions);
      setUploadSummary(normalizedUpload.summary);
      setShowResultsModal(true);

      // Show success message with storage info
      const storedCount = normalizedUpload.summary.storedCount || 0;
      const failedCount = normalizedUpload.summary.failedCount || 0;
      if (storedCount > 0) {
        if (normalizedUpload.summary.isPartialFailure) {
          toast.warning(
            `Upload stored ${storedCount} question${storedCount > 1 ? "s" : ""} with ${failedCount || Math.max(normalizedUpload.summary.parsedCount - storedCount, 0)} issue${(failedCount || normalizedUpload.summary.parsedCount - storedCount) === 1 ? "" : "s"}.`
          );
        } else {
          toast.success(
            `Successfully uploaded and stored ${storedCount} question${
              storedCount > 1 ? "s" : ""
            }!`
          );
        }
      } else {
        toast.warning(
          `Upload completed but no questions were stored. ${failedCount > 0 ? `${failedCount} failed.` : ""}`
        );
      }

      setShowAddQuestionModal(false);
      setUploadedFile(null);
      setMarkdownFile(null);
      setSqlQueryFile(null);
      setSqlQueryField("student_file");
      setSqlSchemaFile(null);
      setSqlInitFile(null);
      setUploadError(null);
      setProblemSets((prev) => {
        const t = uploadTopicName.trim();
        return t ? (prev.includes(t) ? prev : [...prev, t]) : prev;
      });
      // Refresh the questions list after a short delay to ensure DB is updated
      setTimeout(() => {
        fetchQuestions();
      }, 500);
    } catch (error) {
      console.error("Error uploading file:", error);
      if (uploadLanguage === "sql") {
        // Keep the form open and say which file to fix; the backend names it.
        setUploadError(error.message || "The upload failed. Please try again.");
        return;
      }
      toast.error(
        `Failed to upload file: ${error.message || "Please try again."}`
      );

      // If backend returned detailed extraction or source information, surface it in the results modal
      const detail = error.detail || error.extraction_messages || error.extractionMessages || null;
      const practiceQuerySource = error.practice_query_source || error.practiceQuerySource || null;
      const schemaSource = error.schema_source || error.schemaSource || null;

      if (detail || practiceQuerySource || schemaSource) {
        setUploadedQuestions([]);
        setUploadSummary({
          uploadId: null,
          createdQuestionIds: [],
          storedCount: 0,
          failedCount: 0,
          parsedCount: 0,
          storageErrors: Array.isArray(detail) ? detail : [],
          practiceQuerySource: practiceQuerySource,
          schemaSource: schemaSource,
          extractionMessages: detail,
          isPartialFailure: true,
        });
        setShowResultsModal(true);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const closeResultsModal = () => {
    setShowResultsModal(false);
    setUploadedQuestions([]);
    setUploadSummary(null);
  };

  const closeModal = () => {
    setShowAddQuestionModal(false);
    setUploadedFile(null);
    setMarkdownFile(null);
    setSqlQueryFile(null);
    setSqlQueryField("student_file");
    setSqlSchemaFile(null);
    setSqlInitFile(null);
    setUploadError(null);
    setUploadTopicName("");
    setUploadLanguage("python");
  };

  const handleQuestionDeleted = (deletedQuestionId) => {
    // Update the questions list by filtering out the deleted question
    setQuestions((prevQuestions) =>
      prevQuestions.filter((question) => question.id !== deletedQuestionId)
    );
    setSelectedQuestion(null); // Clear selected question after deletion
    toast.success("Question deleted successfully!");
  };

  async function fetchQuestions() {
    const courseId = getCourseId(selectedCourse);
    if (!courseId) {
      return;
    }
    try {
      const fetchedQuestions = await questionService.getProblemsFromCourse(courseId);
      setQuestions(fetchedQuestions);
      console.log("Fetched questions:", fetchedQuestions);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  }

  async function fetchTopics() {
    try {
      const fetchedTopics = await questionService.getTopics();
      setProblemSets(fetchedTopics);
      console.log("Fetched topics:", fetchedTopics);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  }

  useEffect(() => {
    if (selectedQuestion) {
      setIsAddingQuestion(false); // Ensure "Add Question" form doesn't stay active
    }
  }, [selectedQuestion]);

  useEffect(() => {
    fetchQuestions();
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  // Guard: only instructors may access this page
  if (roleData && roleData !== 'instructor') {
    return <PermissionErrorPage />;
  }
  
  console.log("Selected Course in manageQuestions:", selectedCourse);

  const handleUpdatedQuestions = (updatedQuestions) => {
    fetchQuestions();
  };

  const filteredQuestions = questions.filter((question) => {
    // Normalize case for consistency
    const topicNormalized = question.topic.toLowerCase();
    const searchNormalized = searchQuery.toLowerCase();

    // Check if topic matches (or if no filter is applied)
    const matchesTopic =
      activeFilters.length === 0 ||
      activeFilters.some((filter) =>
        topicNormalized.includes(filter.toLowerCase())
      );

    // Check if search matches function name or description
    const matchesSearch =
      searchQuery === "" ||
      question.functionName.toLowerCase().includes(searchNormalized) ||
      question.description.toLowerCase().includes(searchNormalized);

    // Ensure BOTH search and filter work together correctly
    return matchesTopic && matchesSearch;
  });

  // Function to open the preview window
  const openPreviewWindow = (formData) => {
    // Store the current form data in sessionStorage so the popup can access it
    sessionStorage.setItem("previewData", JSON.stringify(formData));

    // Open a new window with the preview page
    const previewWindow = window.open(
      "about:blank",
      "questionPreview",
      "width=900,height=700,resizable=yes,scrollbars=yes"
    );

    // Write the preview HTML directly to the new window
    previewWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Question Preview</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                line-height: 1.6;
                padding: 20px;
                max-width: 1000px;
                margin: 0 auto;
                background-color: #f5f7fa;
              }
              .preview-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 30px;
                padding: 24px 32px;
                background: linear-gradient(135deg, #003057 0%, #004577 100%);
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 48, 87, 0.15);
              }
              .preview-header-info h1 {
                font-size: 1.75rem;
                font-weight: 700;
                color: #ffffff;
                margin: 0 0 6px 0;
              }
              .preview-header-info p {
                color: rgba(255, 255, 255, 0.85);
                margin: 0;
                font-size: 0.95rem;
              }
              .preview-close-btn {
                background: linear-gradient(135deg, #a4925a 0%, #c5b783 100%);
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                font-weight: 600;
                transition: all 0.2s;
                box-shadow: 0 2px 8px rgba(164, 146, 90, 0.3);
              }
              .preview-close-btn:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(164, 146, 90, 0.4);
              }
              .preview-container {
                background-color: #ffffff;
                border-radius: 12px;
                padding: 32px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
              }
              .preview-problem-title {
                font-size: 2rem;
                font-weight: 700;
                margin-bottom: 24px;
                color: #003057;
                padding-bottom: 16px;
                border-bottom: 3px solid #a4925a;
              }
              .preview-section {
                margin-top: 24px;
                margin-bottom: 24px;
              }
              .preview-section-title {
                font-weight: 700;
                font-size: 1.1rem;
                margin-bottom: 10px;
                color: #003057;
              }
              .preview-parameter-value {
                color: #495057;
                line-height: 1.7;
                font-size: 0.95rem;
              }
              .preview-description {
                color: #495057;
                white-space: pre-line;
                line-height: 1.7;
                font-size: 0.95rem;
              }
              .preview-example-box {
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border: 2px solid #003057;
                border-radius: 8px;
                padding: 18px;
                margin-top: 12px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
              }
              .preview-example-code {
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
                white-space: pre-wrap;
                word-wrap: break-word;
                margin: 0;
                font-size: 14px;
                line-height: 1.6;
                color: #003057;
              }
              .preview-code-input {
                border: 2px solid #003057;
                border-radius: 12px;
                padding: 20px;
                margin-top: 24px;
                background-color: #ffffff;
                min-height: 200px;
                box-shadow: 0 2px 8px rgba(0, 48, 87, 0.1);
              }
              .preview-code-line {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 10px;
              }
              .preview-line-number {
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
                color: #6b7280;
                user-select: none;
                min-width: 30px;
                text-align: right;
                font-weight: 600;
              }
              .preview-line-content {
                flex: 1;
                height: 22px;
                border-bottom: 1px solid #d1d5db;
              }
              .preview-note {
                background: linear-gradient(135deg, #fffdf0 0%, #fff9e6 100%);
                border: 2px solid #a4925a;
                border-radius: 8px;
                padding: 14px 18px;
                margin: 20px 0;
                color: #8f7d4a;
                font-weight: 600;
                font-size: 14px;
                box-shadow: 0 2px 4px rgba(164, 146, 90, 0.1);
              }
              .preview-actions-container {
                margin-top: 24px;
                padding: 24px;
                background: #ffffff;
                border-radius: 12px;
                border: 2px solid #003057;
                box-shadow: 0 2px 8px rgba(0, 48, 87, 0.1);
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
              }
              .preview-settings-section {
                display: flex;
                flex-direction: column;
              }
              .preview-settings-section h3 {
                margin-bottom: 10px;
                color: #003057;
                font-size: 1rem;
                font-weight: 700;
              }
              .preview-select-input {
                padding: 10px 14px;
                border: 2px solid #e9ecef;
                border-radius: 8px;
                font-size: 14px;
                background-color: white;
                color: #003057;
                font-weight: 500;
                transition: all 0.2s;
              }
              .preview-select-input:focus {
                outline: none;
                border-color: #a4925a;
              }
              .preview-download-button {
                padding: 10px 14px;
                background: linear-gradient(135deg, #003057 0%, #004577 100%);
                color: white;
                border: none;
                border-radius: 8px;
                cursor: pointer;
                font-weight: 600;
                transition: all 0.2s;
                text-align: center;
                box-shadow: 0 2px 6px rgba(0, 48, 87, 0.2);
              }
              .preview-download-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 10px rgba(0, 48, 87, 0.3);
              }
              .preview-actions-row {
                grid-column: 1 / -1;
                display: flex;
                gap: 12px;
              }
              .preview-submit-btn {
                flex: 3;
                background: linear-gradient(135deg, #a4925a 0%, #c5b783 100%);
                color: white;
                padding: 14px 24px;
                border-radius: 8px;
                border: 2px solid transparent;
                cursor: pointer;
                font-size: 16px;
                font-weight: 700;
                transition: all 0.2s;
                box-shadow: 0 2px 8px rgba(164, 146, 90, 0.3);
              }
              .preview-submit-btn:hover {
                border-color: rgba(197, 183, 131, 0.5);
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(164, 146, 90, 0.4);
              }
              .preview-submit-btn:disabled {
                background: #ccc;
                cursor: not-allowed;
                transform: none;
              }
              .preview-hint-btn {
                flex: 1;
                background: linear-gradient(135deg, #a4925a 0%, #c5b783 100%);
                color: white;
                padding: 14px 24px;
                border-radius: 8px;
                border: 2px solid transparent;
                cursor: pointer;
                font-size: 16px;
                font-weight: 700;
                transition: all 0.2s;
                box-shadow: 0 2px 8px rgba(164, 146, 90, 0.3);
              }
              .preview-hint-btn:hover {
                border-color: rgba(197, 183, 131, 0.5);
              }
              .preview-debug-btn {
                flex: 1;
                background: linear-gradient(135deg, #458177 0%, #5a9d91 100%);
                color: white;
                padding: 14px 24px;
                border-radius: 8px;
                border: 2px solid transparent;
                cursor: pointer;
                font-size: 16px;
                font-weight: 700;
                transition: all 0.2s;
                box-shadow: 0 2px 8px rgba(69, 129, 119, 0.3);
              }
              .preview-debug-btn:hover {
                border-color: rgba(69, 129, 119, 0.4);
              }
              .preview-output-container {
                margin-top: 24px;
                padding: 24px;
                border: 3px solid #003057;
                border-radius: 12px;
                background-color: #ffffff;
                box-shadow: 0 2px 8px rgba(0, 48, 87, 0.1);
                position: relative;
                overflow: hidden;
              }
              .preview-output-container::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 4px;
                background: linear-gradient(90deg, #003057 0%, #a4925a 100%);
              }
              .preview-output-container h3 {
                color: #003057;
                margin-bottom: 16px;
                font-size: 1.1rem;
                font-weight: 700;
                margin-top: 4px;
              }
              .preview-output-text {
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
                font-size: 14px;
                color: #495057;
                white-space: pre-wrap;
                word-wrap: break-word;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                padding: 16px;
                border-radius: 8px;
                border: 1px solid #dee2e6;
                min-height: 60px;
                line-height: 1.6;
              }
              .preview-hint-container {
                margin-top: 24px;
                padding: 24px;
                border: 3px solid #a4925a;
                border-radius: 12px;
                background: linear-gradient(135deg, #fffdf0 0%, #fff9e6 100%);
                box-shadow: 0 2px 8px rgba(164, 146, 90, 0.15);
              }
              .preview-hint-container h3 {
                color: #8f7d4a;
                margin-bottom: 16px;
                font-size: 1.1rem;
                font-weight: 700;
              }
              .preview-hint-text {
                color: #495057;
                line-height: 1.7;
                font-size: 0.95rem;
              }
              .preview-debug-container {
                margin-top: 24px;
                padding: 24px;
                border: 3px solid #003057;
                border-radius: 12px;
                background: linear-gradient(135deg, #e8f4f8 0%, #d4e9f0 100%);
                box-shadow: 0 2px 8px rgba(0, 48, 87, 0.1);
              }
              .preview-debug-container h3 {
                color: #003057;
                margin-bottom: 16px;
                font-size: 1.1rem;
                font-weight: 700;
              }
              .preview-debug-text {
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
                font-size: 14px;
                color: #495057;
                white-space: pre-wrap;
                word-wrap: break-word;
                background-color: white;
                padding: 16px;
                border-radius: 8px;
                border: 1px solid #458177;
                min-height: 60px;
                line-height: 1.6;
              }
              @media (max-width: 768px) {
                body {
                  padding: 10px;
                }
                .preview-container {
                  padding: 20px;
                }
                .preview-problem-title {
                  font-size: 1.5rem;
                }
                .preview-actions-container {
                  grid-template-columns: 1fr;
                }
                .preview-actions-row {
                  flex-direction: column;
                }
              }
            </style>
          </head>
          <body>
            <div class="preview-header">
              <div class="preview-header-info">
                <h1>Student Preview</h1>
                <p>This is how the question will appear to students</p>
              </div>
              <button class="preview-close-btn" onclick="window.close()">Close Preview</button>
            </div>
            
            <div class="preview-container">
              <div id="preview-content">
                Loading preview...
              </div>
            </div>
    
            <script>
              // Get the data from sessionStorage
              const previewData = JSON.parse(sessionStorage.getItem('previewData'));
              
              // Render the preview content
              if (previewData) {
                document.getElementById('preview-content').innerHTML = \`
                  <h2 class="preview-problem-title">\${previewData.functionName}()</h2>
                  
                  <div class="preview-section">
                    <p class="preview-section-title">Parameters:</p>
                    <div class="preview-parameter-value">\${previewData.parameters}</div>
                  </div>
                  
                  <div class="preview-section">
                    <p class="preview-section-title">Returns:</p>
                    <div class="preview-parameter-value">\${previewData.returns}</div>
                  </div>
                  
                  <div class="preview-section">
                    <p class="preview-section-title">Description:</p>
                    <div class="preview-description">\${previewData.description}</div>
                  </div>
                  
                  \${previewData.example ? \`
                    <div class="preview-section">
                      <p class="preview-section-title">Example:</p>
                      <div class="preview-example-box">
                        <pre class="preview-example-code">\${previewData.example}</pre>
                      </div>
                    </div>
                  \` : ''}
                  
                  <div class="preview-code-input">
                    <div class="preview-code-line">
                      <span class="preview-line-number">1</span>
                      <div class="preview-line-content"></div>
                    </div>
                    <div class="preview-code-line">
                      <span class="preview-line-number">2</span>
                      <div class="preview-line-content"></div>
                    </div>
                    <div class="preview-code-line">
                      <span class="preview-line-number">3</span>
                      <div class="preview-line-content"></div>
                    </div>
                  </div>
                  
                  <div class="preview-note">
                    Note: If you would like to see the Python shell output (print statements) for debugging, be sure to call your function in the code block above, just like you would in IDLE or another Python IDE.
                  </div>
                  
                  <div class="preview-actions-container">
                    <div class="preview-settings-section">
                      <h3>How difficult was this question for you?</h3>
                      <select class="preview-select-input">
                        <option value="">Select your rating</option>
                        <option value="Very Easy" \${previewData.difficulty === 'Very Easy' ? 'selected' : ''}>Very Easy</option>
                        <option value="Easy" \${previewData.difficulty === 'Easy' ? 'selected' : ''}>Easy</option>
                        <option value="Medium" \${previewData.difficulty === 'Medium' ? 'selected' : ''}>Medium</option>
                        <option value="Hard" \${previewData.difficulty === 'Hard' ? 'selected' : ''}>Hard</option>
                        <option value="Very Hard" \${previewData.difficulty === 'Very Hard' ? 'selected' : ''}>Very Hard</option>
                      </select>
                    </div>
                    
                    <div class="preview-settings-section">
                      <h3>Download</h3>
                      <button class="preview-download-button" disabled>Download Code</button>
                    </div>
                    
                    <div class="preview-actions-row">
                      <button class="preview-submit-btn" disabled>Submit Code</button>
                      <button class="preview-hint-btn" disabled>Hint</button>
                      <button class="preview-debug-btn" disabled>Show Debug</button>
                    </div>
                  </div>
                  
                  <div class="preview-output-container">
                    <h3>Output:</h3>
                    <div class="preview-output-text">Run the code to see the output here.</div>
                  </div>
                \`;
              } else {
                document.getElementById('preview-content').innerHTML = '<p>No preview data available</p>';
              }
            </script>
          </body>
          </html>
        `);

    // Close the document to finish writing
    previewWindow.document.close();
  };

  return (
    <div className="manage-questions-container">
      <main className="manage-questions-main">
        <aside className="manage-questions-sidebar">
          <div className="manage-questions-search-section">
            <div className="manage-questions-search-container">
              <div className="manage-questions-search-input-wrapper">
                <input
                  type="search"
                  placeholder="Search questions..."
                  className="manage-questions-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  className="manage-questions-search-icon"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </div>
              <button
                className="manage-questions-filter-button"
                onClick={() => setShowFilters(!showFilters)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                {activeFilters.length > 0 && (
                  <span className="manage-questions-filter-badge">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              <button
                className="manage-questions-add-button"
                onClick={handleAddQuestionClick}
                title="Add questions"
                aria-label="Add questions"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="manage-questions-filters-section">
              <div className="manage-questions-filter-group">
                <label className="manage-questions-filter-label">
                  Filter By Problem Set
                </label>
                <select
                  className="manage-questions-select"
                  onChange={(e) => setActiveFilters([e.target.value])}
                  value={activeFilters[0] || ""}
                >
                  <option value="">All Sets</option>
                  {problemSets
                    .sort((a, b) => a.localeCompare(b))
                    .map((topic, idx) => (
                      <option key={idx} value={topic}>
                        {topic}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}

          {activeFilters.length > 0 && (
            <div className="manage-questions-active-filters">
              {activeFilters.map((filter) => (
                <div key={filter} className="manage-questions-filter-tag">
                  {filter}
                  <button
                    onClick={() =>
                      setActiveFilters(
                        activeFilters.filter((f) => f !== filter)
                      )
                    }
                    className="manage-questions-filter-tag-button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="manage-questions-list">
            {filteredQuestions.map((question) => (
              <button
                key={question.id}
                onClick={() => setSelectedQuestion(question)}
                className={`manage-questions-item ${
                  selectedQuestion?.id === question.id ? "active" : ""
                }`}
              >
                <div className="manage-questions-item-title">
                  {question.functionName}
                </div>
                <div className="manage-questions-item-meta">
                  <span className="manage-questions-meta-tag">
                    {question.topic}
                  </span>
                </div>
                <div className="manage-questions-item-divider"></div>
              </button>
            ))}
          </div>
        </aside>

        <div className="manage-questions-content">
          {isAddingQuestion ? (
            <AddQuestion
              onUpdateQuestions={handleUpdatedQuestions}
              problemSets={problemSets}
              setProblemSets={setProblemSets}
              preview={openPreviewWindow}
            />
          ) : selectedQuestion ? (
            <EditQuestion
              question={selectedQuestion}
              setSelectedQuestion={setSelectedQuestion}
              onQuestionDeleted={handleQuestionDeleted}
              problemSets={problemSets}
              setProblemSets={setProblemSets}
              preview={openPreviewWindow}
              allQuestions={questions}
              fetchQuestions={fetchQuestions}
              fetchTopics={fetchTopics}
            />
          ) : (
            <div className="manage-questions-empty-state">
              Select a question to view or edit
            </div>
          )}
        </div>
      </main>

      {/* Add Question Modal */}
      {/* Loading Spinner Overlay */}
      {isUploading && (
        <div className="manage-questions-loading-overlay">
          <div className="manage-questions-loading-content">
            <div className="manage-questions-loading-spinner"></div>
            <p className="manage-questions-loading-text">
              Uploading and processing files...
            </p>
            <p className="manage-questions-loading-subtext">
              This may take a few moments
            </p>
          </div>
        </div>
      )}

      {showAddQuestionModal && (
        <div className="manage-questions-modal-overlay" onClick={closeModal}>
          <div
            className="manage-questions-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="manage-questions-modal-header">
              <h2 className="manage-questions-modal-title">
                Add New Questions
              </h2>
              <button
                className="manage-questions-close-button"
                onClick={closeModal}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="manage-questions-modal-body">
              <p className="manage-questions-modal-description"></p>

              <div className="manage-questions-options-container">
                {/* Manual Option 
                <div 
                  style={{
                    ...styles.optionCard,
                    ...(hoveredCard === 'manual' ? styles.optionCardHover : {})
                  }}
                  onClick={handleManualAdd}
                  onMouseEnter={() => setHoveredCard('manual')}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="manage-questions-option-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                    </svg>
                  </div>
                  <h3 className="manage-questions-option-title">Manual Entry</h3>
                  <p className="manage-questions-option-description">
                    Create a question manually for an existing or new problem set.
                  </p>
                  <div style={styles.optionButton}>
                    <span>Create Manually</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </div>
                */}

                {/* Autograder Option */}
                <div
                  className="manage-questions-option-card"
                >
                  <div className="manage-questions-option-icon">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                  </div>
                  <h3 className="manage-questions-option-title">
                    Upload Homework Files
                  </h3>
                  <p className="manage-questions-option-description">
                    Python: upload your autograder ZIP and instructions file (.md for CS 1301, .py skeleton for CS 2316).
                    SQL (CS 4400): upload the course's three files as they are, with no ZIP needed.
                    We'll parse the questions for you.
                  </p>

                  <div className="manage-questions-upload-section">
                    <div className="manage-questions-upload-label-container">
                      <label className="manage-questions-upload-label">
                        Upload Type:
                      </label>
                      <select
                        value={uploadLanguage}
                        onChange={(e) => {
                          setUploadLanguage(e.target.value);
                          setUploadedFile(null);
                          setMarkdownFile(null);
                          setSqlQueryFile(null);
                          setSqlQueryField("student_file");
                          setSqlSchemaFile(null);
                          setSqlInitFile(null);
                          setUploadError(null);
                        }}
                        className="manage-questions-select"
                      >
                        <option value="python">Python</option>
                        <option value="sql">SQL (CS 4400 practice set)</option>
                      </select>
                    </div>

                    <div className="manage-questions-upload-label-container">
                      <label className="manage-questions-upload-label">
                        Problem Set / Topic Name{" "}
                        <span className="manage-questions-required-asterisk">
                          *
                        </span>
                        :
                      </label>
                      <input
                        type="text"
                        value={uploadTopicName}
                        onChange={(e) => setUploadTopicName(e.target.value)}
                        placeholder={uploadLanguage === "sql" ? "e.g. SQL Practice Set 1" : "e.g. Homework 1"}
                        className="manage-questions-input"
                      />
                    </div>

                    {uploadLanguage === "python" && (
                      <div className="manage-questions-upload-label-container">
                        <label className="manage-questions-upload-label">
                          Autograder ZIP File{" "}
                          <span className="manage-questions-required-asterisk">
                            *
                          </span>
                          :
                        </label>
                        <input
                          type="file"
                          id="autograder-file"
                          accept=".zip"
                          onChange={handleFileUpload}
                          className="manage-questions-file-input"
                        />
                        <label
                          htmlFor="autograder-file"
                          className="manage-questions-file-input-label"
                        >
                          {uploadedFile ? uploadedFile.name : "Choose ZIP File"}
                        </label>
                      </div>
                    )}

                    {uploadLanguage === "python" && (
                      <div className="manage-questions-upload-label-container">
                        <label className="manage-questions-upload-label">
                          Homework Markdown File{" "}
                          <span className="manage-questions-required-asterisk">
                            *
                          </span>
                          :
                        </label>
                        <input
                          type="file"
                          id="markdown-file"
                          accept=".md,.markdown,.txt"
                          onChange={handleMarkdownFileUpload}
                          className="manage-questions-file-input"
                        />
                        <label
                          htmlFor="markdown-file"
                          className="manage-questions-file-input-label"
                        >
                          {markdownFile
                            ? markdownFile.name
                            : "Choose Markdown File"}
                        </label>
                      </div>
                    )}

                    {uploadLanguage === "sql" && (
                      <>
                        <p className="manage-questions-upload-hint">
                          Upload one practice set at a time. The init file and student template are the
                          same for every set; only the question prompts change. Do not upload the answer key.{" "}
                          <a
                            href="https://github.gatech.edu/BrainBoost/its-web-app/blob/main/docs/instructors/cs4400-practice-sets.md"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Step-by-step guide
                          </a>
                        </p>

                        <div className="manage-questions-upload-label-container">
                          <label className="manage-questions-upload-label">
                            Question Prompts (PDF){" "}
                            <span className="manage-questions-required-asterisk">
                              *
                            </span>
                            :
                          </label>
                          <input
                            type="file"
                            id="sql-instructions-file"
                            accept=".pdf,.md,.markdown,.txt"
                            onChange={handleSqlInstructionsFileUpload}
                            className="manage-questions-file-input"
                          />
                          <label
                            htmlFor="sql-instructions-file"
                            className="manage-questions-file-input-label"
                          >
                            {sqlQueryFile ? sqlQueryFile.name : "Choose PDF"}
                          </label>
                          <p className="manage-questions-upload-hint">
                            The practice set document, e.g. SQL Practice Set 1.pdf. Each question starts with its name, like practiceQuery11.
                          </p>
                        </div>

                        <div className="manage-questions-upload-label-container">
                          <label className="manage-questions-upload-label">
                            Init File (.sql){" "}
                            <span className="manage-questions-required-asterisk">
                              *
                            </span>
                            :
                          </label>
                          <input
                            type="file"
                            id="sql-init-file"
                            accept=".sql,.txt"
                            onChange={handleSqlInitFileUpload}
                            className="manage-questions-file-input"
                          />
                          <label
                            htmlFor="sql-init-file"
                            className="manage-questions-file-input-label"
                          >
                            {sqlInitFile ? sqlInitFile.name : "Choose Init File"}
                          </label>
                          <p className="manage-questions-upload-hint">
                            From the course package, e.g. company_practice_init.sql. It builds the database and the grader.
                          </p>
                        </div>

                        <div className="manage-questions-upload-label-container">
                          <label className="manage-questions-upload-label">
                            Student Template (.sql){" "}
                            <span className="manage-questions-required-asterisk">
                              *
                            </span>
                            :
                          </label>
                          <input
                            type="file"
                            id="sql-source-file"
                            accept=".sql,.txt"
                            onChange={handleSqlExamFileUpload}
                            className="manage-questions-file-input"
                          />
                          <label
                            htmlFor="sql-source-file"
                            className="manage-questions-file-input-label"
                          >
                            {sqlSchemaFile ? sqlSchemaFile.name : "Choose Student Template"}
                          </label>
                          <p className="manage-questions-upload-hint">
                            From the course package, e.g. company_practice_student.sql. The blank file students write their answers in.
                          </p>
                        </div>
                      </>
                    )}

                    {uploadError && (
                      <div className="manage-questions-upload-error" role="alert">
                        <strong>Nothing was uploaded.</strong> {uploadError}
                      </div>
                    )}

                    <button
                      className="manage-questions-upload-button"
                      onClick={handleAutograderUpload}
                      disabled={
                        isUploading ||
                        !uploadTopicName.trim() ||
                        (uploadLanguage === "python"
                          ? (!uploadedFile || !markdownFile)
                          : (!sqlQueryFile || !sqlInitFile || !sqlSchemaFile))
                      }
                    >
                      {isUploading ? (
                        <>
                          <span className="manage-questions-upload-button-icon">
                            ⏳
                          </span>
                          Uploading...
                        </>
                      ) : (
                        "Upload Questions"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Results Modal */}
      {showResultsModal && (
        <div className="manage-questions-modal-overlay" onClick={closeResultsModal}>
          <div
            className="manage-questions-results-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="manage-questions-modal-header">
              <h2 className="manage-questions-modal-title">Upload Results</h2>
              <button className="manage-questions-close-button" onClick={closeResultsModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="manage-questions-modal-body">
              <p className="manage-questions-modal-description">
                {uploadSummary?.isPartialFailure ? (
                  <>
                    Parsed <strong>{uploadSummary.parsedCount}</strong> {uploadSummary.parsedCount === 1 ? "question" : "questions"}, but only <strong>{uploadSummary.storedCount}</strong> were stored successfully.
                  </>
                ) : (
                  <>
                    Successfully parsed <strong>{uploadSummary?.parsedCount || uploadedQuestions.length}</strong> {(uploadSummary?.parsedCount || uploadedQuestions.length) === 1 ? "question" : "questions"} from the uploaded files.
                  </>
                )}
              </p>

              {uploadSummary?.storedCount > 0 && (
                <AutograderStatus uploadId={uploadSummary.uploadId} />
              )}

              <div className="manage-questions-results-meta" style={{ marginBottom: "1rem" }}>
                <div className="manage-questions-detail-row">
                  <strong>Upload ID:</strong>
                  <span>{uploadSummary?.uploadId || "N/A"}</span>
                </div>
                <div className="manage-questions-detail-row">
                  <strong>Stored Count:</strong>
                  <span>{uploadSummary?.storedCount || 0}</span>
                </div>
                <div className="manage-questions-detail-row">
                  <strong>Failed Count:</strong>
                  <span>{uploadSummary?.failedCount || 0}</span>
                </div>
                <div className="manage-questions-detail-row">
                  <strong>Created Question IDs:</strong>
                  <span>{(uploadSummary?.createdQuestionIds || []).length > 0 ? (uploadSummary.createdQuestionIds || []).join(", ") : "N/A"}</span>
                </div>
                <div className="manage-questions-detail-row">
                  <strong>Practice Query Source:</strong>
                  <span>{uploadSummary?.practiceQuerySource || "N/A"}</span>
                </div>
                <div className="manage-questions-detail-row">
                  <strong>Schema Source:</strong>
                  <span>{uploadSummary?.schemaSource || "N/A"}</span>
                </div>
              </div>

              {uploadSummary?.extractionMessages && (
                <div className="manage-questions-extraction-messages" style={{ marginBottom: "1rem" }}>
                  <strong>Extraction Details:</strong>
                  <pre className="manage-questions-code-block" style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{String(uploadSummary.extractionMessages)}</pre>
                </div>
              )}

              {uploadSummary?.isPartialFailure && (
                <div className="manage-questions-error-text" style={{ marginBottom: "1rem", display: "block" }}>
                  One or more queries were not stored. Review the inline errors below.
                </div>
              )}

              <div className="manage-questions-results-container">
                {uploadedQuestions.map((question, index) => {
                  const questionErrors = getQuestionStorageErrors(question, uploadSummary?.storageErrors || []);
                  const testCases = parseMaybeJson(question.testCases) || [];
                  const testCaseDisplay = stringifyMaybeJson(question.testCases);
                  const parametersDisplay = stringifyMaybeJson(question.parameters);
                  const returnsDisplay = stringifyMaybeJson(question.returns);
                  const schemaDisplay = stringifyMaybeJson(question.schema);
                  const hasDescription = Boolean((question.description || "").toString().trim());
                  const hasExample = Boolean((question.example || "").toString().trim());
                  const createdQuestionIdSet = new Set((uploadSummary?.createdQuestionIds || []).map((value) => String(value)));
                  const hasStoredMatch = question.createdQuestionId
                    ? createdQuestionIdSet.has(String(question.createdQuestionId))
                    : false;
                  const shouldShowInlineFailure =
                    questionErrors.length > 0 ||
                    (uploadSummary?.isPartialFailure && !hasStoredMatch);

                  return (
                    <div key={`${question.order || index + 1}-${question.functionName || index}`} className="manage-questions-question-card">
                      <h3 className="manage-questions-question-title">
                        practiceQuery{question.order || index + 1}: {question.functionName || "Unknown Function"}
                      </h3>

                      <div className="manage-questions-question-details">
                        <div className="manage-questions-detail-row">
                          <strong>Upload ID:</strong>
                          <span>{question.upload_id || uploadSummary?.uploadId || "N/A"}</span>
                        </div>

                        <div className="manage-questions-detail-row">
                          <strong>Created Question ID:</strong>
                          <span>{question.createdQuestionId || "N/A"}</span>
                        </div>

                        <div className="manage-questions-detail-row">
                          <strong>Function Name:</strong>
                          <span>{question.functionName || "N/A"}</span>
                        </div>

                        <div className="manage-questions-detail-section">
                          <strong>Description:</strong>
                          {hasDescription ? (
                            <div className="manage-questions-markdown-content">{question.description}</div>
                          ) : (
                            <div className="manage-questions-empty-field">{question.functionName || "No description provided"}</div>
                          )}
                        </div>

                        {hasExample && (
                          <div className="manage-questions-detail-section">
                            <strong>Example:</strong>
                            <pre className="manage-questions-code-block">{question.example}</pre>
                          </div>
                        )}

                        <div className="manage-questions-detail-section">
                          <strong>Schema:</strong>
                          {schemaDisplay ? (
                            <pre className="manage-questions-code-block">{schemaDisplay}</pre>
                          ) : (
                            <div className="manage-questions-empty-field">No schema provided</div>
                          )}
                        </div>

                        <div className="manage-questions-detail-section">
                          <strong>Parameters:</strong>
                          {parametersDisplay ? (
                            <pre className="manage-questions-code-block">{parametersDisplay}</pre>
                          ) : (
                            <div className="manage-questions-empty-field">None</div>
                          )}
                        </div>

                        <div className="manage-questions-detail-section">
                          <strong>Returns:</strong>
                          {returnsDisplay ? (
                            <pre className="manage-questions-code-block">{returnsDisplay}</pre>
                          ) : (
                            <div className="manage-questions-empty-field">Unknown</div>
                          )}
                        </div>

                        <div className="manage-questions-detail-section">
                          <strong>Test Cases ({Array.isArray(testCases) ? testCases.length : 0}):</strong>
                          {testCaseDisplay ? (
                            <pre className="manage-questions-code-block">{testCaseDisplay}</pre>
                          ) : (
                            <div className="manage-questions-empty-field">No test cases found</div>
                          )}
                        </div>

                        {shouldShowInlineFailure && (
                          <div className="manage-questions-detail-section">
                            <strong>Storage Errors:</strong>
                            {questionErrors.length > 0 ? (
                              questionErrors.map((error, errorIndex) => (
                                <div key={`${question.functionName || question.order}-${errorIndex}`} className="manage-questions-empty-field" style={{ marginTop: "0.5rem" }}>
                                  {error.error || error.detail || "Unknown storage error"}
                                  {error.detail ? `: ${error.detail}` : ""}
                                </div>
                              ))
                            ) : (
                              <div className="manage-questions-empty-field" style={{ marginTop: "0.5rem" }}>
                                This question was not stored successfully. Review the upload summary for details.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="manage-questions-modal-footer">
                <button className="manage-questions-button" onClick={closeResultsModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddQuestionForm;

function EditQuestion({
  question,
  setSelectedQuestion,
  onQuestionDeleted,
  problemSets,
  setProblemSets,
  preview,
  allQuestions,
  fetchQuestions,
  fetchTopics,
}) {
  const [formData, setFormData] = useState({
    ...question,
    ccrn: question.ccrn,
  });
  const [newTopic, setNewTopic] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [problemSetQuestions, setProblemSetQuestions] = useState([]);

  useEffect(() => {
    console.log("Updated question:", question);
    setFormData({ ...question, ccrn: question.ccrn});
    console.log("form data:", formData);
  }, [question]); // change fields when question changes

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "questionDifficulty" ? parseInt(value, 10) : value, // Convert only for questionDifficulty
    }));
    console.log(name, value);
  };

  const handleEditorChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteQuestion = async () => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      // Check if question has submissions
      const submissions = await submissionService.getSubmissionsFromQuestion(formData.questionID);
      if (submissions && submissions.length > 0) {
        alert("This question has submissions and cannot be deleted.");
        return;
      }
    } catch (error) {
      console.error("Error checking submissions:", error);
    }

    try {
      await questionService.deleteProblemById(formData.questionID);
      await fetchTopics();
      await fetchQuestions();
      onQuestionDeleted(formData.questionID);
    } catch (error) {
      console.error("Error deleting question:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteProblemSet = async () => {
    if (!window.confirm("Are you sure you want to delete this entire problem set? This will delete ALL questions in this problem set.")) {
      return;
    }

    console.log("=== Starting Problem Set Deletion ===");
    console.log("Problem set topic:", formData.topic);
    console.log("All questions in course:", allQuestions);
    let setQuestions = [];

    // Step 1: Find all questions in this problem set and check for submissions
    console.log("Step 1: Checking questions in problem set...");
    for (const q of allQuestions) {
      if (q.topic == formData.topic) {
        console.log(`Found question in set: ${q.functionName} (ID: ${q.questionID})`);
        try {
          const sub = await submissionService.getSubmissionsFromQuestion(q.questionID);
          console.log(`Submissions for question ${q.questionID}:`, sub);
          if (sub.length != 0) {
            setQuestions = [];
            console.error("❌ Deletion aborted: Problem set has submissions");
            alert("This problem set has submissions and cannot be deleted.");
            return;
          } else {
            setQuestions.push(q);
          }
        } catch (error) {
          console.error(`Error checking submissions for question ${q.questionID}:`, error);
          alert(`Error: ${error.message}`);
        }
      }
    }

    console.log(`Found ${setQuestions.length} questions to delete in problem set "${formData.topic}"`);
    console.log("Questions to delete:", setQuestions.map(q => ({ id: q.questionID, name: q.functionName })));

    try {
      // Step 2: Delete all questions in the set
      console.log("Step 2: Deleting individual questions...");
      let deletedCount = 0;
      let failedCount = 0;

      for (const q of setQuestions) {
        try {
          console.log(`Deleting question ${q.questionID} (${q.functionName})...`);
          await questionService.deleteProblemById(q.questionID);
          console.log(`✅ Successfully deleted question ${q.questionID}`);
          deletedCount++;
        } catch (error) {
          console.error(`❌ Error deleting question ${q.questionID}:`, error);
          failedCount++;
          alert(`Error deleting question ${q.functionName}: ${error.message}`);
        }
      }

      console.log(`Question deletion summary: ${deletedCount} succeeded, ${failedCount} failed`);

      // Step 3: Delete the problem set itself
      console.log("Step 3: Deleting problem set from database...");
      console.log("Problem set topic to delete:", question.topic);
      const responseData = await problemSetService.deleteProblemSetByTopic(question.topic, question.ccrn);
      console.log("✅ Problem set deletion response data:", responseData);
      console.log("=== Problem Set Deletion Complete ===");

      toast.success("Problem set successfully deleted!");
      setSelectedQuestion(null);
      
      // Refresh the questions and topics lists
      console.log("Refreshing questions and topics...");
      await fetchQuestions();
      await fetchTopics();

    } catch (error) {
        console.error("❌ Error in problem set deletion process:", error);
        console.error("Error type:", error.constructor.name);
        console.error("Error message:", error.message);
        console.error("Full error object:", error);
        alert(`Error: ${error.message}`);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      "functionName",
      "parameters",
      "returns",
      "description",
      "topic",
      "example",
      "testCases",
      "solution",
      "questionDifficulty",
    ];

    // Check for missing fields
    const emptyFields = requiredFields.filter((field) => !formData[field]);

    if (emptyFields.length > 0) {
      alert(
        `Please fill in all fields before submitting: ${emptyFields.join(", ")}`
      );
      return;
    }

    try {
      await questionService.updateProblemById(formData.questionID, formData);
      setFormData((prev) => ({
        ...prev,
        topic: formData.topic, // force re-assign to trigger re-render if needed
      }));

      setSelectedQuestion((prev) => ({
        ...prev,
        topic: formData.topic,
      }));

      alert("Question updated successfully!");
    } catch (error) {
      console.error("Error updating question:", error);
      alert("Error updating question");
    }
  };

  const handleAddNewSet = () => {
    if (!newTopic.trim()) return;

    // Add it locally
    setProblemSets((prev) => [...new Set([...prev, newTopic])]);

    // Update formData.topic so the new topic is selected
    setFormData((prev) => ({
      ...prev,
      topic: newTopic,
    }));

    setNewTopic("");
    setShowInput(false);
  };

  return (
    <form className="manage-questions-form" onSubmit={handleSubmit}>
      <div className="manage-questions-heading">Edit Question</div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Function Name</label>
        <input
          type="text"
          name="functionName"
          value={formData.functionName}
          onChange={handleChange}
          className="manage-questions-input"
        />
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Parameters</label>
        <input
          type="text"
          name="parameters"
          value={formData.parameters}
          onChange={handleChange}
          className="manage-questions-input"
        />
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Returns</label>
        <input
          type="text"
          name="returns"
          value={formData.returns}
          onChange={handleChange}
          className="manage-questions-input"
        />
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Question Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="manage-questions-textarea"
        />
      </div>
      <div className="manage-questions-topic-section">
        <div className="manage-questions-topic-header">
          <label className="manage-questions-label">Problem Set</label>
          {!showInput && (
            <button
              type="button"
              onClick={() => setShowInput(true)}
              className="manage-questions-new-topic-button"
            >
              + New
            </button>
          )}
        </div>

        {showInput && (
          <div className="manage-questions-new-topic-input-container">
            <input
              type="text"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              placeholder="Enter problem set name"
              className="manage-questions-input"
            />
            <button
              type="button"
              onClick={() => {
                handleAddNewSet();
                setShowInput(false);
                setNewTopic("");
              }}
              className="manage-questions-create-topic-button"
            >
              Create
            </button>
          </div>
        )}

        <select
          name="topic"
          value={formData.topic}
          onChange={handleChange}
          className="manage-questions-select"
        >
          {problemSets.map((topic, idx) => (
            <option key={idx} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Example</label>
        <div className="manage-questions-editor-container">
          <Editor
            height="200px"
            value={formData.example}
            defaultLanguage="json"
            theme="light"
            onChange={(value) => handleEditorChange("example", value)}
          />
        </div>
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Test Cases</label>

        <div className="manage-questions-editor-container">
          {/*                      <Editor
                         height="200px"
                         value={formData.testCases}
                         defaultLanguage="json"
                         theme="light"
                         onChange={(value) => handleEditorChange("testCases", value)}
                     /> */}
        </div>
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Solution</label>

        <div className="manage-questions-editor-container">
          {/* //                     <Editor
                         height="200px"
                         value={formData.solution}
                         defaultLanguage="json"
                         theme="light"
                         onChange={(value) => handleEditorChange("solution", value)}
                     /> */}
        </div>
      </div>
      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Difficulty</label>
        <select
          name="questionDifficulty"
          value={formData.questionDifficulty}
          onChange={handleChange}
          className="manage-questions-select"
        >
          <option value="1">Easy</option>
          <option value="2">Medium</option>
          <option value="3">Hard</option>
        </select>
      </div>
      <div className="manage-questions-button-group">
        <button
          type="button"
          onClick={() => preview(formData)}
          className="manage-questions-button"
        >
          Preview Question
        </button>
        <button type="submit" className="manage-questions-button">
          Save Changes
        </button>
        <button
          type="button"
          className="manage-questions-button-outline"
          onClick={handleDeleteQuestion}
        >
          Delete Question
        </button>
        <button
          type="button"
          className="manage-questions-button-outline"
          onClick={handleDeleteProblemSet}
        >
          Delete Problem Set
        </button>
      </div>
    </form>
  );
}

function AddQuestion({
  onUpdateQuestions,
  problemSets,
  setProblemSets,
  preview,
}) {
  const { selectedCourse } = useCourse();
  const [topic, setTopic] = useState("Functions");
  const [functionName, setFunctionName] = useState("exampleName");
  const [description, setDescription] = useState("Description of the problem");
  const [parameters, setParameters] = useState(
    "example1 (str), example2 (bool)"
  );
  const [returns, setReturns] = useState("example (bool)");
  const [example, setExample] = useState("");
  const [testCases, setTestCases] = useState("");
  const [questionDifficulty, setDifficulty] = useState("1");
  const [solution, setSolution] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [showInput, setShowInput] = useState(false);

  const handleDiff = (event) => {
    setDifficulty(parseInt(event.target.value, 10));
  };

  const questionData = {
    description,
    example,
    functionName,
    parameters,
    returns,
    testCases,
    topic,
    questionDifficulty: parseInt(questionDifficulty),
    questionOrigin: "Instructor",
    ccrn: getCourseId(selectedCourse),
    solution,
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = {
      functionName,
      parameters,
      returns,
      description,
      topic,
      example,
      testCases,
      solution,
      questionDifficulty,
    };

    // Check for missing fields
    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value.trim()) // Ensures no empty values
      .map(([key, _]) => key);

    if (emptyFields.length > 0) {
      alert(
        `Please fill in all fields before submitting: ${emptyFields.join(", ")}`
      );
      return;
    }

    console.log("Submitting:", JSON.stringify(questionData, null, 2)); // Debugging

    try {
      const data = await questionService.storeProblem(questionData);
      alert("Problem added successfully!");
      setTopic("");
      setFunctionName("exampleName");
      setDescription("Description of the problem");
      setParameters("example1 (str), example2 (bool)");
      setReturns("example (bool)");
      setExample("");
      setTestCases("");
      setDifficulty("");
      setSolution("");

      onUpdateQuestions(questionData);
    } catch (error) {
      console.error("Error submitting problem:", error);
      alert(error.message || "Failed to submit problem.");
    }
  };

  const handleAddNewSet = () => {
    if (!newTopic.trim()) return;

    // Add the new topic to the list if it's not already there
    setProblemSets((prev) => [...new Set([...prev, newTopic])]);

    // Update the selected topic to the new one
    setTopic(newTopic);

    // Clear input and hide
    setNewTopic("");
    setShowInput(false);
  };

  return (
    <form className="manage-questions-form">
      <div className="manage-questions-heading">Add New Question</div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Function Name</label>
        <input
          type="text"
          value={functionName}
          onChange={(e) => setFunctionName(e.target.value)}
          className="manage-questions-input"
        />
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Parameters</label>
        <input
          type="text"
          value={parameters}
          onChange={(e) => setParameters(e.target.value)}
          className="manage-questions-input"
        />
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Returns</label>
        <input
          type="text"
          value={returns}
          onChange={(e) => setReturns(e.target.value)}
          className="manage-questions-input"
        />
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Question Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="manage-questions-textarea"
        />
      </div>

      <div className="manage-questions-topic-section">
        <div className="manage-questions-topic-header">
          <label className="manage-questions-label">Problem Set</label>
          {!showInput && (
            <button
              type="button"
              onClick={() => setShowInput(true)}
              className="manage-questions-new-topic-button"
            >
              + New
            </button>
          )}
        </div>

        {showInput && (
          <div className="manage-questions-new-topic-input-container">
            <input
              type="text"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              placeholder="Enter problem set name"
              className="manage-questions-input"
            />
            <button
              type="button"
              onClick={() => {
                handleAddNewSet();
                console.log(problemSets);
              }}
              className="manage-questions-create-topic-button"
            >
              Create
            </button>
          </div>
        )}

        <select
          name="topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="manage-questions-select"
        >
          {problemSets.map((topic, idx) => (
            <option key={idx} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Example</label>
        <div className="manage-questions-editor-container">
          <Editor
            height="200px"
            value={example}
            onChange={(value) => setExample(value)}
            defaultLanguage="json"
            theme="light"
          />
        </div>
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Test Cases</label>
        <div className="manage-questions-editor-container">
          {/*                <Editor 
     height="200px" 
                 value={testCases} 
                 onChange={(value) => setTestCases(value)} 
                 defaultLanguage="json" 
                 theme="light"
               /> */}
        </div>
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Solution</label>
        <div className="manage-questions-editor-container">
          {/*                <Editor 
                 height="200px" 
                 value={solution} 
                 onChange={(value) => setSolution(value)} 
                 defaultLanguage="json" 
                 theme="light"
               /> */}
        </div>
      </div>

      <div className="manage-questions-form-group">
        <label className="manage-questions-label">Difficulty</label>
        <select
          value={questionDifficulty}
          className="manage-questions-select"
          onChange={handleDiff}
        >
          <option value="1">Easy</option>
          <option value="2">Medium</option>
          <option value="3">Hard</option>
        </select>
      </div>

      <div className="manage-questions-button-group">
        <button
          type="button"
          onClick={() => preview(questionData)}
          className="manage-questions-button"
        >
          Preview Question
        </button>
        <button
          type="submit"
          className="manage-questions-button"
          onClick={handleSubmit}
        >
          Add Question
        </button>
      </div>
    </form>
  );
}
